import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import type { StoryboardClip, StoryboardProject } from "./types";
import {
  type StudioClipReview, type StudioReviewClip,
  requestClipApprove, requestClipRegen, requestClipReorder, requestClipSelect,
  requestReviewApprove, requestReviewAssemble, requestReviewReplace, requestReviewReroll, requestReviewSelect, requestReviewUseAllClean,
} from "./studioBus";
import { useAuthMediaUrl } from "@/lib/authMedia";
import { clipKindLabel, qcFlagged } from "./clipKind";
import RenderGate, { useBoardRender } from "./RenderGate";
import MediaZoomViewer, { type ZoomItem } from "./MediaZoomViewer";
import ReasonChips, { useReasonPicks } from "./ReasonChips";

// MobileReviewSheet — the storyboard approval and the clip review as a
// FULL-WIDTH list on a phone. Below 768px the canvas is a 180-260px strip
// (DirectorConsole styles, `.ldc-canvas` at 560px), and a 36-scene approval
// or a 36-clip review cannot be judged in a strip you pan and zoom. This sheet
// opens over the workspace with one scene per row and the same buttons the
// board has; every action goes over the SAME studio-bus events, so the flow
// that owns the API calls (CorridoChatFlow) is the only place work starts.
// Desktop is untouched: this component renders nothing above 767px.

const NARROW_MAX_PX = 767;

/** True while the viewport is phone-width. Reads innerWidth (jsdom-safe; no
 *  matchMedia dependency) and follows resizes. */
export function useNarrowViewport(): boolean {
  const [narrow, setNarrow] = useState(() => typeof window !== "undefined" && window.innerWidth <= NARROW_MAX_PX);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () => setNarrow(window.innerWidth <= NARROW_MAX_PX);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return narrow;
}

const S = {
  // Drawn on the page itself (a portal), above the console (200) and the site
  // navbar: inside the board it sat UNDER the console header, which covered its
  // title and Close. Below the dialogs (400).
  sheet: { position: "fixed", inset: 0, zIndex: 300, background: "#0a0e15", color: "#e2e8f0", display: "flex", flexDirection: "column", fontFamily: "system-ui, sans-serif" } as React.CSSProperties,
  header: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "12px 14px", borderBottom: "1px solid #223046", flexShrink: 0 } as React.CSSProperties,
  title: { fontSize: 14, fontWeight: 800, letterSpacing: 1, textTransform: "uppercase", color: "#94a3b8" } as React.CSSProperties,
  list: { flex: 1, overflowY: "auto", padding: "8px 12px 96px", display: "grid", gap: 8 } as React.CSSProperties,
  row: { display: "flex", gap: 10, alignItems: "flex-start", background: "#101722", border: "1px solid #2a3648", borderRadius: 12, padding: 10 } as React.CSSProperties,
  thumb: { width: 56, height: 100, borderRadius: 8, background: "#000", overflow: "hidden", flexShrink: 0 } as React.CSSProperties,
  footer: { position: "absolute", left: 0, right: 0, bottom: 0, display: "flex", flexWrap: "wrap", gap: 8, padding: 12, background: "rgba(10,14,21,.96)", borderTop: "1px solid #223046" } as React.CSSProperties,
  input: { background: "#05070b", border: "1px solid #2a3648", borderRadius: 8, color: "#e2e8f0", fontSize: 13, minWidth: 0, padding: "8px 10px", width: "100%", boxSizing: "border-box" } as React.CSSProperties,
};
const btn = (kind: "primary" | "ok" | "ghost" | "accent", disabled = false): React.CSSProperties => ({
  borderRadius: 9, fontSize: 13, fontWeight: 700, padding: "9px 12px", cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.5 : 1, minHeight: 40,
  ...(kind === "primary" ? { background: "#38bdf8", border: "1px solid transparent", color: "#04121e" }
    : kind === "ok" ? { background: "rgba(52,211,153,.14)", border: "1px solid #34d399", color: "#34d399" }
    : kind === "accent" ? { background: "rgba(56,189,248,.1)", border: "1px solid #38bdf8", color: "#fff" }
    : { background: "transparent", border: "1px solid #2a3648", color: "#e2e8f0" }),
});

/** Tapping the still opens it full screen in the deep-zoom viewer. */
function Thumb({ url, onZoom }: { url: string | null; onZoom?: () => void }) {
  const { t } = useTranslation();
  const resolved = useAuthMediaUrl(url || "");
  const img = resolved && <img src={resolved} alt="" loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover" }} />;
  if (!onZoom) return <div style={S.thumb}>{img}</div>;
  return (
    <button type="button" aria-label={t("studio.zoom.open")} data-testid="mobile-thumb-zoom"
      onClick={(e) => { e.stopPropagation(); onZoom(); }}
      style={{ ...S.thumb, border: "none", cursor: "zoom-in", padding: 0, position: "relative" }}>
      {img}
      <span aria-hidden style={{ background: "rgba(0,0,0,.72)", borderRadius: 999, bottom: 4, color: "#fff", fontSize: 12, lineHeight: "20px", position: "absolute", right: 4, textAlign: "center", width: 20 }}>⤢</span>
    </button>
  );
}

/** One storyboard scene: approve, regenerate (with an optional note), move. */
function BoardRow({ clip, position, total, selected, anyRegenerating, onMove, onZoom }: {
  clip: StoryboardClip; position: number; total: number; selected: boolean; anyRegenerating: boolean;
  onMove: (idx: number, dir: -1 | 1) => void;
  onZoom: () => void;
}) {
  const { t } = useTranslation();
  const [note, setNote] = useState("");
  // "What's wrong?" chips, on the selected row only; cleared when it is left.
  const [reasons, setReasons, clearReasons] = useReasonPicks(selected);
  const approved = clip.status === "approved";
  const redrawing = clip.status === "regenerating";
  const order = (dir: -1 | 1) => onMove(clip.idx, dir);
  return (
    <div data-testid={`mobile-scene-${clip.idx}`} style={{ ...S.row, borderColor: approved ? "#34d399" : selected ? "#38bdf8" : "#2a3648" }} onClick={() => requestClipSelect(clip.idx)}>
      <Thumb url={clip.imageUrl} onZoom={onZoom} />
      <div style={{ flex: 1, minWidth: 0, display: "grid", gap: 6 }}>
        <div style={{ fontSize: 13, fontWeight: 700 }}>
          {t("studio.mobile.sceneN", { n: position + 1 })}
          <span style={{ color: "#7c8aa0", fontWeight: 400 }}> · {clipKindLabel(t, clip.kind)}</span>
          {redrawing && <span style={{ color: "#e3b341", fontWeight: 700 }}> · {t("studio.timeline.regenerating")}</span>}
        </div>
        {clip.theme && <div style={{ color: "#94a3b8", fontSize: 12 }}>{clip.theme}</div>}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          <button type="button" data-testid={`mobile-approve-${clip.idx}`} style={btn(approved ? "ok" : "ghost")}
            onClick={(e) => { e.stopPropagation(); requestClipApprove(clip.idx); }}>
            {approved ? t("studio.timeline.approved") : t("studio.timeline.approveClip")}
          </button>
          <button type="button" data-testid={`mobile-regen-${clip.idx}`} style={btn("accent", anyRegenerating)} disabled={anyRegenerating}
            onClick={(e) => { e.stopPropagation(); requestClipRegen(clip.idx, note.trim() || undefined, selected ? reasons : undefined); setNote(""); clearReasons(); }}>
            {redrawing ? t("studio.timeline.regenerating") : note.trim() ? t("studio.timeline.redoWithDirection") : t("studio.timeline.regenerate")}
          </button>
          <button type="button" style={btn("ghost", position <= 0)} disabled={position <= 0} onClick={(e) => { e.stopPropagation(); order(-1); }}>{t("studio.timeline.earlier")}</button>
          <button type="button" style={btn("ghost", position >= total - 1)} disabled={position >= total - 1} onClick={(e) => { e.stopPropagation(); order(1); }}>{t("studio.timeline.later")}</button>
        </div>
        {selected && (
          <ReasonChips kind="stills" value={reasons} onChange={setReasons} disabled={anyRegenerating} testId={`mobile-reasons-${clip.idx}`} />
        )}
        {selected && (
          <input value={note} onChange={(e) => setNote(e.target.value)} onClick={(e) => e.stopPropagation()}
            placeholder={t("studio.timeline.steerPlaceholder")} style={S.input} />
        )}
      </div>
    </div>
  );
}

/** One animated clip: watch (when selected), approve, new take, replace scene. */
function ReviewRow({ clip, position, approved, selected, busy, rerolling, rerollsLeft, onZoom }: {
  clip: StudioReviewClip; position: number; approved: boolean; selected: boolean; busy: boolean; rerolling: boolean; rerollsLeft: number;
  onZoom: () => void;
}) {
  const { t } = useTranslation();
  const [steer, setSteer] = useState("");
  // "What's wrong?" chips for the selected clip (shown under its player).
  const [reasons, setReasons, clearReasons] = useReasonPicks(selected);
  const video = useAuthMediaUrl(selected ? clip.videoUrl : "");
  const flagged = qcFlagged(clip.qc);
  return (
    <div data-testid={`mobile-clip-${clip.idx}`} data-qc={flagged ? "flagged" : "ok"} style={{ ...S.row, flexDirection: "column", borderColor: approved ? "#34d399" : flagged ? "#e3b341" : selected ? "#38bdf8" : "#2a3648" }} onClick={() => requestReviewSelect(clip.idx)}>
      <div style={{ display: "flex", gap: 10, width: "100%" }}>
        <Thumb url={clip.thumbUrl} onZoom={onZoom} />
        <div style={{ flex: 1, minWidth: 0, display: "grid", gap: 6 }}>
          <div style={{ fontSize: 13, fontWeight: 700 }}>
            {t("studio.timeline.clipN", { n: position + 1 })}
            <span style={{ color: "#7c8aa0", fontWeight: 400 }}> · {clipKindLabel(t, clip.kind)}</span>
          </div>
          {flagged && <div data-testid={`mobile-qc-${clip.idx}`} style={{ color: "#e3b341", fontSize: 12 }}>{t("studio.canvas.qcFlagged", { problems: (clip.qc?.problems || []).join("; ") })}</div>}
          {clip.theme && <div style={{ color: "#94a3b8", fontSize: 12 }}>{clip.theme}</div>}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            <button type="button" data-testid={`mobile-use-${clip.idx}`} style={btn(approved ? "ok" : "ghost")} onClick={(e) => { e.stopPropagation(); requestReviewApprove(clip.idx); }}>
              {approved ? t("studio.canvas.inTheVideo") : t("studio.canvas.useThisClip")}
            </button>
            <button type="button" data-testid={`mobile-newtake-${clip.idx}`} style={btn("accent", busy || rerollsLeft <= 0)} disabled={busy || rerollsLeft <= 0}
              onClick={(e) => { e.stopPropagation(); requestReviewReroll({ idx: clip.idx, reasons: selected ? reasons : [] }); clearReasons(); }}>
              {rerolling ? t("studio.canvas.rendering") : t("studio.canvas.newTake")}
            </button>
          </div>
        </div>
      </div>
      {selected && (
        <>
          {video
            ? <video src={video} controls playsInline loop style={{ aspectRatio: "9 / 16", background: "#000", borderRadius: 10, width: "100%", maxHeight: 360, objectFit: "contain" }} />
            : <div style={{ aspectRatio: "9 / 16", background: "#000", borderRadius: 10, width: "100%", maxHeight: 360 }} />}
          <ReasonChips kind="clips" value={reasons} onChange={setReasons} disabled={busy || rerollsLeft <= 0} testId={`mobile-clip-reasons-${clip.idx}`} />
          <div style={{ display: "flex", gap: 6, width: "100%" }}>
            <input value={steer} onChange={(e) => setSteer(e.target.value)} onClick={(e) => e.stopPropagation()} placeholder={t("studio.canvas.steerPlaceholder")} style={S.input} />
            <button type="button" data-testid={`mobile-replace-${clip.idx}`} style={{ ...btn("accent", busy || rerollsLeft <= 0), whiteSpace: "nowrap" }} disabled={busy || rerollsLeft <= 0}
              onClick={(e) => { e.stopPropagation(); requestReviewReplace({ idx: clip.idx, scene: steer.trim() || undefined, reasons }); setSteer(""); clearReasons(); }}>
              {t("studio.canvas.replaceScene")}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default function MobileReviewSheet({ live, review }: { live: StoryboardProject | null; review: StudioClipReview | null }) {
  const { t } = useTranslation();
  const narrow = useNarrowViewport();
  const [open, setOpen] = useState(false);
  const [viewerIdx, setViewerIdx] = useState<number | null>(null);
  const boardRender = useBoardRender();
  const boardEditable = !!live && live.phase === "board" && live.clips.length > 0;
  const hasReview = !!review && review.clips.length > 0;
  // Open by itself the moment something needs a decision (a fresh board, a
  // fresh set of clips); the customer can close it and reopen from the pill.
  const wantKey = hasReview ? `review:${review!.runId}` : boardEditable ? `board:${live!.runId}` : "";
  useEffect(() => { if (wantKey) setOpen(true); }, [wantKey]);
  // "Earlier"/"Later" on a row: the same order list the desktop inspector sends.
  const moveScene = (idx: number, dir: -1 | 1) => {
    if (!live) return;
    const order = live.clips.map((c) => c.idx);
    const pos = order.indexOf(idx);
    const target = pos + dir;
    if (pos < 0 || target < 0 || target >= order.length) return;
    [order[pos], order[target]] = [order[target], order[pos]];
    requestClipReorder(order);
  };

  if (!narrow || (!boardEditable && !hasReview)) return null;
  if (!open) {
    return (
      <button type="button" data-testid="mobile-sheet-open" onClick={() => setOpen(true)}
        style={{ position: "absolute", left: "50%", transform: "translateX(-50%)", bottom: 60, zIndex: 8, ...btn("primary"), boxShadow: "0 10px 24px rgba(0,0,0,.45)" }}>
        {t("studio.mobile.open")}
      </button>
    );
  }

  const viewer = (items: ZoomItem[]) => viewerIdx !== null && viewerIdx < items.length && (
    <MediaZoomViewer items={items} index={viewerIdx} onIndex={setViewerIdx} onClose={() => setViewerIdx(null)} />
  );

  if (hasReview && review) {
    const approvedSet = new Set(review.approved);
    const busy = review.busy || review.rerolling !== null;
    return createPortal(
      <div data-testid="mobile-review-sheet" style={S.sheet}>
        <div style={S.header}>
          <div style={S.title}>{t("studio.mobile.reviewTitle", { count: review.clips.length })}</div>
          <button type="button" data-testid="mobile-sheet-close" style={btn("ghost")} onClick={() => setOpen(false)}>{t("studio.mobile.close")}</button>
        </div>
        <div style={S.list}>
          <div style={{ color: "#7c8aa0", fontSize: 12 }}>{t("studio.mobile.hint")} · {t("studio.canvas.rerollsLeft", { count: review.rerollsLeft })}</div>
          {review.clips.map((c, pos) => (
            <ReviewRow key={c.idx} clip={c} position={pos} approved={approvedSet.has(c.idx)} selected={review.selectedIdx === c.idx}
              busy={busy} rerolling={review.rerolling === c.idx} rerollsLeft={review.rerollsLeft} onZoom={() => setViewerIdx(pos)} />
          ))}
        </div>
        {viewer(review.clips.map((c, i) => ({ src: c.videoUrl || c.thumbUrl, kind: c.videoUrl ? "video" : "image", label: t("studio.timeline.clipN", { n: i + 1 }) })))}
        <div style={S.footer}>
          <button type="button" data-testid="mobile-use-all-clean" style={btn("ok")} onClick={() => requestReviewUseAllClean()}>{t("studio.canvas.useAllClean")}</button>
          <button type="button" data-testid="mobile-assemble" style={{ ...btn("primary", review.approved.length < 4 || busy), flex: 1 }} disabled={review.approved.length < 4 || busy}
            onClick={() => requestReviewAssemble()} title={review.approved.length < 4 ? t("studio.canvas.pickAtLeastFour") : undefined}>
            {t("studio.canvas.buildMyVideo", { count: review.approved.length })}
          </button>
        </div>
      </div>,
      document.body,
    );
  }

  const clips = live!.clips;
  const allApproved = clips.every((c) => c.status === "approved");
  const anyRegenerating = clips.some((c) => c.status === "regenerating");
  const cost = live!.renderCostCredits ?? null;
  return createPortal(
    <div data-testid="mobile-board-sheet" style={S.sheet}>
      <div style={S.header}>
        <div style={S.title}>{t("studio.mobile.boardTitle", { count: clips.length })}</div>
        <button type="button" data-testid="mobile-sheet-close" style={btn("ghost")} onClick={() => setOpen(false)}>{t("studio.mobile.close")}</button>
      </div>
      <div style={S.list}>
        <div style={{ color: "#7c8aa0", fontSize: 12 }}>{t("studio.mobile.hint")}</div>
        {clips.map((c, pos) => (
          <BoardRow key={c.id} clip={c} position={pos} total={clips.length} selected={live!.selectedIdx === c.idx} anyRegenerating={anyRegenerating} onMove={moveScene} onZoom={() => setViewerIdx(pos)} />
        ))}
      </div>
      {viewer(clips.map((c, i) => ({ src: c.imageUrl, kind: "image", label: t("studio.mobile.sceneN", { n: i + 1 }) })))}
      <div style={S.footer}>
        {/* The price question and any refusal, inside the sheet (full width). */}
        <div style={{ flexBasis: "100%" }}><RenderGate board={live!} unheard={boardRender.unheard} /></div>
        {!allApproved && (
          <button type="button" data-testid="mobile-approve-all" style={btn("ok")}
            onClick={() => clips.forEach((c) => { if (c.status !== "approved") requestClipApprove(c.idx); })}>
            {t("studio.timeline.approveAll", { count: clips.length })}
          </button>
        )}
        <button type="button" data-testid="mobile-render" style={{ ...btn("primary", !allApproved || anyRegenerating), flex: 1 }} disabled={!allApproved || anyRegenerating}
          // The sheet stays open: the price to confirm shows right above
          // this button (RenderGate). Closing it sent the customer to look
          // for the question in the chat panel underneath.
          onClick={boardRender.render} title={allApproved ? undefined : t("studio.timeline.approveAllToUnlock")}>
          {allApproved
            ? live!.renderFree ? t("studio.timeline.renderContinueFree")
            : (cost && cost > 0 ? t("studio.timeline.renderWithCost", { cost: cost.toLocaleString() }) : t("studio.timeline.renderNoCost"))
            : t("studio.timeline.awaitingApproval", { count: clips.filter((c) => c.status !== "approved").length })}
        </button>
      </div>
    </div>,
    document.body,
  );
}
